/**
 * WebhookTemplatePicker — "Start from a template" Select above the body textarea.
 * On pick: applies body + merges headers. If textarea already has content,
 * shows an inline ConfirmDialog before overwriting.
 */
import { useState } from 'react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import { WEBHOOK_TEMPLATES } from './webhook-templates';

// Sentinel: never a real template id, never empty string (Radix throws on "").
const SENTINEL = '__none__';

interface Props {
  currentBody: string;
  currentHeaders: Record<string, string>;
  onApply: (body: string, mergedHeaders: Record<string, string>) => void;
}

export function WebhookTemplatePicker({ currentBody, currentHeaders, onApply }: Props) {
  const [pendingId, setPendingId] = useState<string | null>(null);

  const applyTemplate = (id: string) => {
    const tpl = WEBHOOK_TEMPLATES.find((t) => t.id === id);
    if (!tpl) return;
    const mergedHeaders = tpl.headers
      ? { ...tpl.headers, ...currentHeaders }
      : currentHeaders;
    onApply(tpl.body, mergedHeaders);
  };

  const handleSelect = (id: string) => {
    if (id === SENTINEL) return;
    if (currentBody.trim()) {
      // Body has content — confirm before overwrite
      setPendingId(id);
    } else {
      applyTemplate(id);
    }
  };

  const pendingTemplate = pendingId ? WEBHOOK_TEMPLATES.find((t) => t.id === pendingId) : null;

  return (
    <>
      <Field
        label="Start from a template"
        description="Applies a starter body. Existing custom headers are preserved."
      >
        {() => (
          <Select value={SENTINEL} onValueChange={handleSelect}>
            <SelectTrigger style={{ maxWidth: 360 }}>
              <SelectValue placeholder="Choose a template…" />
            </SelectTrigger>
            <SelectContent>
              {WEBHOOK_TEMPLATES.map((tpl) => (
                <SelectItem key={tpl.id} value={tpl.id}>
                  <span style={{ fontWeight: 500 }}>{tpl.label}</span>
                  <span
                    style={{
                      marginLeft: 8,
                      color: 'var(--vn-fg-muted)',
                      fontSize: 'var(--vn-font-size-xs)',
                    }}
                  >
                    {tpl.description}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>

      <ConfirmDialog
        open={!!pendingId}
        onOpenChange={(open) => { if (!open) setPendingId(null); }}
        title="Replace current body?"
        description={
          pendingTemplate
            ? `Apply the "${pendingTemplate.label}" template? Your current body will be replaced. Custom headers will be preserved.`
            : 'Apply this template? Your current body will be replaced.'
        }
        confirmLabel="Apply template"
        onConfirm={() => {
          if (pendingId) applyTemplate(pendingId);
          setPendingId(null);
        }}
      />
    </>
  );
}
